// src/pages/ApplyInstructor.js
import React, { useState } from 'react';
import { auth, db } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';

function ApplyInstructor() {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [form, setForm] = useState({
    bio: '',
    sports: '',
    experience: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const uid = auth.currentUser.uid;
      const userRef = doc(db, 'users', uid);
      const applicationId = `APP-${uid.slice(0, 6).toUpperCase()}`; // ✅ Create a simple unique ref

      await updateDoc(userRef, {
        applicationStatus: 'pending',
        applicationCode: applicationId,
        instructorApplication: {
          bio: form.bio,
          sports: form.sports,
          experience: form.experience,
          submittedAt: new Date().toISOString(),
        },
      });

      navigate('/dashboard');
    } catch (err) {
      console.error('Error submitting application:', err);
      alert('❌ Error submitting. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // 🔒 Already approved
  if (userData?.applicationStatus === 'approved') {
    return (
      <div className="max-w-md mx-auto p-4 text-center">
        <h2 className="text-xl font-bold mb-2">You're Already Approved!</h2>
        <p className="text-gray-600">
          Your instructor profile has already been approved. You now have access to instructor tools.
        </p>
      </div>
    );
  }

  // 🕒 Pending
  if (userData?.applicationStatus === 'pending') {
    return (
      <div className="max-w-md mx-auto p-4 text-center">
        <h2 className="text-xl font-bold mb-2">Application Already Submitted</h2>
        <p className="text-gray-600">
          We’ve received your instructor application. Please wait for our review and email confirmation.
        </p>
      </div>
    );
  }

  // ❌ Rejected
  if (userData?.applicationStatus === 'rejected') {
    return (
      <div className="max-w-md mx-auto p-4 text-center border border-red-300 bg-red-50 rounded">
        <h2 className="text-xl font-bold mb-2 text-red-600">Application Rejected</h2>
        <p className="text-gray-700">
          Your previous instructor application was not approved.
          If you’d like to reapply, please contact us directly and include the reference code below.
        </p>
        <p className="mt-4 font-mono text-sm text-red-700">
          Reference Code: <strong>{userData.applicationCode || 'N/A'}</strong>
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-md mx-auto p-4 space-y-4 border rounded shadow"
    >
      <h2 className="text-2xl font-semibold mb-2">Apply to Become an Instructor</h2>

      <textarea
        name="bio"
        value={form.bio}
        onChange={handleChange}
        placeholder="Short bio (who are you?)"
        className="w-full p-2 border rounded"
        required
      />

      <select
        name="sports"
        value={form.sports}
        onChange={handleChange}
        className="w-full p-2 border rounded"
        required
      >
        <option value="">Select sport you coach</option>
        <option value="ski">Ski</option>
        <option value="snowboard">Snowboard</option>
      </select>

      <input
        name="experience"
        value={form.experience}
        onChange={handleChange}
        placeholder="Years of experience"
        className="w-full p-2 border rounded"
        required
      />

      <button
        type="submit"
        disabled={submitting}
        className="bg-blue-600 text-white p-2 rounded w-full"
      >
        {submitting ? 'Submitting...' : 'Submit Application'}
      </button>
    </form>
  );
}

export default ApplyInstructor;
